// What "the selection" means when Hide is pressed.
//
// There are TWO selections in this app and I wrongly assumed there was
// one. The viewer keeps `totalSelection`, maintained by clicks,
// ctrl-clicks and selectUids. But the tracking page keeps its OWN -
// ModelTracking's `selected`, painted through highlightUids - and its
// box, tree and quantities picks never pass through selectUids at all.
// So on the page the owner actually asked about, the box said "Selected
// in the model: 687" while totalSelection was EMPTY and Hide acted on
// nothing (root-8e, headless on staging main.142043be, 2026-09-22).
//
// Hence a union, and hence this being a function rather than two lines
// inside the viewer: the rule is "a person's selection is whatever is
// LIT for them, wherever it is held", and that deserves a name and a
// test rather than being an expression nobody can exercise.
//
// ORDER IS STABLE AND THE VIEWER'S COMES FIRST, so a repeated uid keeps
// its first position; nothing depends on the order today, and a set that
// reshuffles between calls is the kind of thing that makes a later bug
// impossible to reproduce.

/**
 * ARRAY OR SET. totalSelection is an array today, but the viewer's own
 * tests hand this a Set and an Array.isArray gate would have accepted
 * them while quietly framing nothing - a check that cannot fail is worse
 * than no check. A string is deliberately NOT iterated: one uid passed by
 * mistake must not become 36 single characters.
 *
 * @param {string[]|Set<string>|null} totalSelection the viewer's own selection
 * @param {string[]|Set<string>|null} pageSelection  what the page has lit, if it offers it
 * @returns {string[]} de-duplicated, falsy entries dropped
 */
export function selectionForHide(totalSelection, pageSelection) {
  const out = [];
  const seen = new Set();
  for (const list of [totalSelection, pageSelection]) {
    if (!list || typeof list === 'string') continue;
    if (typeof list[Symbol.iterator] !== 'function') continue;
    for (const uid of list) {
      if (!uid || seen.has(uid)) continue;
      seen.add(uid);
      out.push(uid);
    }
  }
  return out;
}

/**
 * Ask the page what it has lit, without trusting it to exist or behave.
 * A page that offers no pageSelection is the normal case, not an error -
 * only tracking holds a second selection today.
 */
export function pageSelectionOf(reactApi) {
  if (!reactApi || typeof reactApi.pageSelection !== 'function') return [];
  try {
    const got = reactApi.pageSelection();
    if (Array.isArray(got)) return got;
    // A Set counts too - selectionForHide takes either, and a page that
    // keeps its lit set as a Set should not silently light nothing.
    if (got instanceof Set) return [...got];
    return [];
  } catch (e) {
    // A page whose selection getter throws must not break Hide for the
    // viewer's own selection.
    return [];
  }
}
